//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { QueryRenderer, ReadyState } from "react-relay";
// event-scheduler-room-input-renderer imports
import { EventSchedulerRoomInput } from "./EventSchedulerRoomInput";
import { query } from "./EventSchedulerRoomInputRenderer.gql";
import { IRendererContainer, IRendererProps, IRendererState } from "./EventSchedulerRoomInputTypes";
// other internals
import { RoomOptionEnum } from "#webapp/src/components/event-scheduler";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
// state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// util
import { logger } from "#webapp/src/util/logger.util";
import { handleReadyStateError } from "#webapp/src/util/relay.util";
import { getCurrentWorkingGroup } from "#webapp/src/util/working-group.util";

//////////////////
// COMPONENT
//////////////////
class EventSchedulerRoomInputRendererComponent extends React.PureComponent<
  IRendererProps,
  IRendererState
> {
  private cachedOrg: ISchema.IOrg | null = null;
  private shouldForce = false;

  constructor(props: IRendererProps) {
    super(props);
    this.state = {
      boundaryError: false,
      key: 0,
      open: !!props.open,
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IRendererProps) {
    if (nextProps.lastRoomSettingsMillis > this.props.lastRoomSettingsMillis) {
      // increment key to force a rerender, and set shouldForce to true to
      // actually make a new request to graphql for this update
      this.shouldForce = true;
      this.setState({ key: this.state.key + 1 });
    }
    if (nextProps.open) {
      this.setState({ open: true });
    }
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error("componentDidCatch in EventSchedulerRoomInputRenderer", error, info);

    // Display fallback UI
    this.setState({ boundaryError: true });
  }

  private onEditModeChange = (open: boolean) => {
    this.setState({ open });
  };

  public render() {
    const {
      orgId,
      invitees,
      minutesDuration,
      timeZone,
      minSlot,
      maxSlot,
      includeRoom,
      roomsOverride,
      limit,
      seenSlots,
    } = this.props;

    const inputQuery = {
      invitees,
      minutesDuration,
      timeZone,
      minSlot,
      maxSlot,
      roomsOverride,
      limit,
      seenSlots,
      includeRoom: includeRoom !== RoomOptionEnum.WithoutRoom,
    };

    return (
      <QueryRenderer
        key={this.state.key}
        environment={this.props.environment}
        query={query}
        variables={{
          orgRelayId: orgId,
          query: inputQuery,
        }}
        cacheConfig={{
          force: this.shouldForce,
        }}
        render={(readyState: ReadyState) => {
          const freshOrg = readyState.props && readyState.props.node;
          const loadingForTheFirstTime = !this.cachedOrg && !freshOrg;

          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "EventSchedulerRoomInputRenderer");

            return <div>An error has occurred. Please try refreshing the page</div>;
          }

          if (loadingForTheFirstTime) {
            // loading for the first time
            return (
              <div style={{}}>
                <div
                  style={{
                    padding: "4px 0",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </div>
              </div>
            );
          }

          // cache an org so it doesn't flash on new loads
          if (freshOrg) {
            this.cachedOrg = freshOrg;
          }

          const org = freshOrg || this.cachedOrg;
          const workingGroup = getCurrentWorkingGroup(org);

          this.shouldForce = false;
          return (
            <EventSchedulerRoomInput
              schedulingResponseErrorable={org.schedulingResponseErrorable}
              workingGroup={workingGroup}
              roomIds={this.props.roomIds}
              selectedSlot={this.props.selectedSlot}
              onChange={this.props.onChange}
              open={this.state.open}
              onEditModeChange={this.onEditModeChange}
              useAutoSchedule={this.props.useAutoSchedule}
            />
          );
        }}
      />
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IRendererContainer) {
  return {
    lastRoomSettingsMillis: state.settings.lastRoomSettingsMillis,
  };
}

export const EventSchedulerRoomInputRenderer = connect(
  mapStateToProps,
  {},
)(EventSchedulerRoomInputRendererComponent);
